import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Dayjs } from 'dayjs';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '../../../componentsv2/Typography';
import Form from '../../../componentsv2/Form';
import { TextField, SelectField, DateField } from '../../../componentsv2/Field';
import StepButtons from './StepButtons';
import { useTranslation } from '../../../hooks/i18n';
import { COLORS } from '../../../themev2';
import { CountryKeys } from '../../../types';
import { US_COUNTRY_CODE, getBusinessStructureOptions, getUSStates } from '../../../constants/data';

interface CompanyInformationProps {
  country: CountryKeys;
  initialValues?: FormValues;
  onSubmit: (values: FormValues) => void;
  onBack: () => void;
  isLoading?: boolean;
}

export interface FormValues {
  companyName: string;
  entityType: string;
  formationDate: Dayjs | null;
  taxIdType: string;
  taxId: string;
  businessStreetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

const CompanyInformation = ({
  country,
  initialValues,
  onSubmit,
  onBack,
  isLoading,
}: CompanyInformationProps) => {
  const { t } = useTranslation(['activation', 'common']);

  const normalizedCountry = country.trim().toLowerCase();
  const isUnitedStates = normalizedCountry === US_COUNTRY_CODE;
  const taxIdTypeByCountry = isUnitedStates ? 'EIN' : 'RFC';
  const taxIdTypeOptions = [{ label: taxIdTypeByCountry, value: taxIdTypeByCountry }];

  const validationSchema = Yup.object({
    companyName: Yup.string().required(t('common:requiredField')),
    entityType: Yup.string().required(t('common:requiredField')),
    formationDate: Yup.mixed().required(t('common:requiredField')).nullable(),
    taxIdType: Yup.string().oneOf(['EIN', 'RFC']).required(t('common:requiredField')),
    taxId: Yup.string().required(t('common:requiredField')),
    businessStreetAddress: Yup.string().required(t('common:requiredField')),
    city: Yup.string().required(t('common:requiredField')),
    state: Yup.string().required(t('common:requiredField')),
    zipCode: Yup.string().required(t('common:requiredField')),
  });

  const defaultValues: FormValues = {
    companyName: '',
    entityType: '',
    formationDate: null,
    taxIdType: taxIdTypeByCountry,
    taxId: '',
    businessStreetAddress: '',
    city: '',
    state: '',
    zipCode: '',
  };
  const formInitialValues: FormValues = {
    ...defaultValues,
    ...initialValues,
    taxIdType: taxIdTypeByCountry,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={(values: FormValues) => {
          onSubmit({
            ...values,
            taxIdType: taxIdTypeByCountry,
          });
        }}
        enableReinitialize
      >
        {({ submitForm }: FormikProps<FormValues>) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Grid container rowSpacing={3}>
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '36px', color: COLORS.brand.night }}
                >
                  {t('activation:step2.title')}
                </Typography>
                <Typography
                  sx={{ fontSize: '18px', fontWeight: 400, lineHeight: '27px', color: COLORS.gray[1100] }}
                >
                  {t('activation:step2.subtitle')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Form name="company-information-form" id="company-information-form">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="companyName"
                        label={t('activation:step2.fields.companyName')}
                        placeholder={t('activation:step2.placeholders.companyName')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectField
                        name="entityType"
                        label={t('activation:step2.fields.entityType')}
                        placeholder={t('activation:step2.placeholders.entityType')}
                        options={getBusinessStructureOptions(t, country)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DateField
                        name="formationDate"
                        label={t('activation:step2.fields.yearOfIncorporation')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={0}>
                        <Grid item xs={12}>
                          <Typography sx={{ padding: '8px 0', color: COLORS.gray[1200] }}>
                            {t('activation:step2.fields.taxIdType')}
                          </Typography>
                        </Grid>
                        <Grid item xs={5} md={4} lg={3} xl={2}>
                          <SelectField
                            name="taxIdType"
                            options={taxIdTypeOptions}
                            disabled
                            sx={{
                              '& fieldset': {
                                borderRadius: '8px 0 0 8px',
                                borderColor: COLORS.gray[900],
                              },
                            }}
                            hideErrorText
                          />
                        </Grid>
                        <Grid item xs={7} md={8} lg={9} xl={10}>
                          <TextField
                            name="taxId"
                            placeholder={t('activation:step2.placeholders.taxId')}
                            sx={{
                              '& fieldset': {
                                borderRadius: '0 8px 8px 0',
                                borderColor: COLORS.gray[900],
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="businessStreetAddress"
                        label={t('activation:step2.fields.businessAddress')}
                        placeholder={t('activation:step2.placeholders.businessAddress')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="city"
                        label={t('activation:step2.fields.city')}
                        placeholder={t('activation:step2.placeholders.city')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {isUnitedStates ? (
                        <SelectField
                          name="state"
                          label={t('activation:step2.fields.state')}
                          placeholder={t('activation:step2.placeholders.state')}
                          options={getUSStates.map(({ stateKey, state }) => ({
                            value: stateKey,
                            label: state,
                          }))}
                        />
                      ) : (
                        <TextField
                          name="state"
                          label={t('activation:step2.fields.state')}
                          placeholder={t('activation:step2.placeholders.state')}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="zipCode"
                        label={t('activation:step2.fields.zipCode')}
                        placeholder={t('activation:step2.placeholders.zipCode')}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </Grid>
            </Grid>
            <StepButtons onNext={submitForm} onBack={onBack} isNextLoading={isLoading} />
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default CompanyInformation;
